import { isApiError } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, shortDuration } from '../../utils/format'
import type { ReportDetailTab, ReportDetailWindow as WindowDays } from '../../utils/reportDetailPath'
import { GROUP_MEMBERS_NOT_RESOLVABLE } from '../governance/governanceFormat'
import { IDLE_RUN_MEANING, reasonLabel } from '../retirement/retirementFormat'
import type {
  ReportDetailBackgroundExports,
  ReportDetailDay,
  ReportDetailDurations,
  ReportDetailExportFailure,
  ReportDetailOutcomeWindow,
  ReportDetailOwner,
  ReportDetailOverview,
  ReportDetailReport,
  ReportDetailRetirement,
  ReportDetailSchedule,
  ReportDetailSource,
  ReportDetailSourceFailures,
  ReportDetailSubscriptions,
  ReportDetailWindow,
} from '../../types/reportDetail'

/**
 * Presentation for Report Detail (pages/ReportDetailPage.tsx and the seven tabs).
 *
 * Every helper is a WORDING decision, never a calculation: the server selects,
 * counts, ranks the percentiles, maps every stored literal to a name and says
 * why each null is null. Nothing here recomputes any of it, and nothing here
 * turns a null into a zero — a null is NOT_MEASURED with the served reason.
 *
 * Imports only utils/format (which imports only services/apiError), two
 * sibling format modules that do the same, and types, so it stays testable in
 * node.
 */

/** What RestrictedState names when the caller lacks Lighthouse.Admin. */
export const REPORT_DETAIL_SURFACE = 'Report Detail'

/** The tab strip's labels, in the order REPORT_DETAIL_TABS declares. No Lineage: there are no semantic models. */
export const TAB_LABEL: Readonly<Record<ReportDetailTab, string>> = {
  overview: 'Overview',
  governance: 'Governance',
  operations: 'Operations',
  usage: 'Usage',
  scheduling: 'Scheduling',
  access: 'Access',
  history: 'History',
}

/** The tabs that read the run log, and so offer the window chooser. */
export function isWindowedTab(tab: ReportDetailTab): boolean {
  return tab === 'operations' || tab === 'usage'
}

/** A window chip's label. */
export function windowChipLabel(days: WindowDays): string {
  return `${days} days`
}

// ---------------------------------------------------------------------------
// Instants
// ---------------------------------------------------------------------------

/** A served instant's UTC day, `yyyy-MM-dd`; null for null or an unreadable value. */
export function utcDay(iso: string | null): string | null {
  if (iso == null) return null
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------
// The run-log window
// ---------------------------------------------------------------------------

/**
 * The heading every run figure sits under, from what the log MEASURABLY covers.
 *
 * - Complete: "Last 30 days".
 * - Partial: "Since 2026-09-20 UTC" — the log begins inside the window, so the
 *   figures start where it begins. NEVER "last N days": the trimmed days are not
 *   days of no use, they are days nobody can see any more.
 * - None: the log is empty, so there is nothing to title — NOT_MEASURED.
 */
export function windowTitle(window: ReportDetailWindow): string {
  if (window.coverage === 'Complete') return `Last ${window.requestedDays} days`
  if (window.coverage === 'Partial') {
    const day = utcDay(window.effectiveStart)
    return day ? `Since ${day} UTC` : NOT_MEASURED
  }
  return NOT_MEASURED
}

// ---------------------------------------------------------------------------
// Figures
// ---------------------------------------------------------------------------

/** A figure and, when it is not measured, why. */
export interface FigureView {
  value: string
  reason: string | null
}

/** A served count, or NOT_MEASURED. Null is never 0. */
export function countText(value: number | null | undefined): string {
  return value == null ? NOT_MEASURED : exactNumber(value)
}

/**
 * What an Interactive row count IS: rows, never "runs" and never "opens" — before the run log's cutover each Spyglass
 * grid page was its own row, and a row count spans both meanings. Opens are a separate figure, counted only inside the
 * outcome window.
 */
export const INTERACTIVE_LABEL = 'rows recorded'

/** Why an unrecordable report's Interactive figures are NOT_MEASURED — never 0. */
export const INTERACTIVE_NOT_RECORDED = 'opens of a report with no parameters are not recorded'

/** What one row of each source is NOW, by the server's `recordedAs`; the unit caption says what it was before. */
const RECORDED_AS_LABEL: Readonly<Record<string, string>> = {
  Opens: 'one row per open',
  Deliveries: 'one row per delivery',
  Exports: 'one row per export',
}

/** A source row's unit. A `recordedAs` this build does not know is shown as the server sent it. */
export function recordedAsLabel(recordedAs: string): string {
  return RECORDED_AS_LABEL[recordedAs] ?? recordedAs
}

// ---------------------------------------------------------------------------
// The outcome window (decision R2, 2026-10-01)
// ---------------------------------------------------------------------------

/**
 * The heading every OUTCOME figure (failures, opens, deliveries, interactive timings) sits under.
 *
 * - No outcome window: NOT_MEASURED — the cutover date is unset, has not arrived, or the log is empty.
 * - It covers the whole window: the window's own title ("Last 30 days", "Since … UTC").
 * - It starts inside the window: "Since <the cutover's day> UTC", never "Last N days".
 */
export function outcomesTitle(outcomes: ReportDetailOutcomeWindow, window: ReportDetailWindow): string {
  if (outcomes.from == null) return NOT_MEASURED
  if (outcomes.coversWholeWindow) return windowTitle(window)
  const day = utcDay(outcomes.from)
  return day ? `Since ${day} UTC` : NOT_MEASURED
}

/** How the outcome window stands, for the banner's tone and a test hook. */
export type OutcomesState = 'unavailable' | 'partial' | 'whole'

export function outcomesState(outcomes: ReportDetailOutcomeWindow): OutcomesState {
  if (outcomes.from == null) return 'unavailable'
  return outcomes.coversWholeWindow ? 'whole' : 'partial'
}

/** The outcome banner's text: the served caption, or why there is no outcome window. */
export function outcomesText(outcomes: ReportDetailOutcomeWindow): string {
  return outcomes.caption ?? outcomes.unavailableReason ?? 'Outcomes are not counted for this window.'
}

/**
 * An open or delivery count inside the outcome window, or NOT_MEASURED with why. A null here is never 0: without a
 * cutover date the log cannot tell an open from a grid page.
 */
export function outcomeCountView(value: number | null | undefined, reason: string | null | undefined): FigureView {
  return value == null
    ? { value: NOT_MEASURED, reason: reason ?? 'not served' }
    : { value: exactNumber(value), reason: null }
}

/**
 * A column of outcome counts, named for where it counts from: "Opens since 2026-09-20 UTC" when the outcome window
 * starts inside the page's window, the bare noun when it covers it (the card's own title says the window) or when
 * there is none (every cell is then NOT_MEASURED, and the reason is said once above the table).
 */
export function outcomeColumnHeader(noun: string, outcomes: Pick<ReportDetailOutcomeWindow, 'from' | 'coversWholeWindow'>): string {
  const day = utcDay(outcomes.from)
  return day && !outcomes.coversWholeWindow ? `${noun} since ${day} UTC` : noun
}

/** "2 of them before 2026-09-20 UTC" under a row count; null when there are none, or no date separates them. */
export function rowsBeforeText(
  source: Pick<ReportDetailSource, 'rowsBeforeOutcomes'>,
  outcomes: Pick<ReportDetailOutcomeWindow, 'from'>,
): string | null {
  const n = source.rowsBeforeOutcomes
  const day = utcDay(outcomes.from)
  if (n == null || n === 0 || day == null) return null
  return `${exactNumber(n)} of them before ${day} UTC`
}

/**
 * The Interactive figure.
 *
 * When `interactiveRecordable` is false the value is NOT_MEASURED with
 * {@link INTERACTIVE_NOT_RECORDED} WHATEVER the row carries: an open of a report
 * with no parameters writes no row, so a zero there would claim nobody opened it.
 * The server already serves null in that case; this holds even if it did not.
 */
export function interactiveView(value: number | null | undefined, recordable: boolean, reason: string | null): FigureView {
  if (!recordable) return { value: NOT_MEASURED, reason: INTERACTIVE_NOT_RECORDED }
  if (value == null) return { value: NOT_MEASURED, reason: reason ?? 'not served' }
  return { value: exactNumber(value), reason: null }
}

/** Any other source's count, with the served reason when null. */
export function sourceCountView(source: ReportDetailSource): FigureView {
  return source.rowCount == null
    ? { value: NOT_MEASURED, reason: source.rowCountUnavailableReason ?? 'not served' }
    : { value: exactNumber(source.rowCount), reason: null }
}

// ---------------------------------------------------------------------------
// Failures — counted inside the outcome window, a rate only from 20 runs, never pooled
// ---------------------------------------------------------------------------

/**
 * "3 of 41 failed" for one source inside the outcome window — served whenever the counts are, below the rate's
 * floor included. NOT_MEASURED with the served reason when they are not: without a cutover date every older row was
 * written as a success, so a "0 failed" there would be a claim nobody measured.
 */
export function failedOfView(failures: Pick<ReportDetailSourceFailures, 'recorded' | 'failed' | 'countsUnavailableReason'>): FigureView {
  if (failures.recorded == null || failures.failed == null) {
    return { value: NOT_MEASURED, reason: failures.countsUnavailableReason ?? 'not served' }
  }
  return { value: `${exactNumber(failures.failed)} of ${exactNumber(failures.recorded)} failed`, reason: null }
}

/**
 * The failure rate as the server computed it, to one decimal — or NOT_MEASURED with why: below 20 recorded runs of
 * the source, or with no outcome window. Never computed here: the page divides nothing.
 */
export function failureRateView(
  failures: Pick<ReportDetailSourceFailures, 'failureRatePercent' | 'failureRateUnavailableReason' | 'countsUnavailableReason'>,
): FigureView {
  if (failures.failureRatePercent == null) {
    return { value: NOT_MEASURED, reason: failures.failureRateUnavailableReason ?? failures.countsUnavailableReason ?? 'not served' }
  }
  return { value: `${failures.failureRatePercent.toFixed(1)}%`, reason: null }
}

/**
 * One day's failures, "interactive · scheduled · export" — NOT_MEASURED on a day whose rows record no outcome (before
 * the cutover, or with none set). An Interactive figure for a report whose opens are not recorded is NOT_MEASURED too.
 */
export function dayFailedText(day: Pick<ReportDetailDay, 'outcomesRecorded' | 'interactiveFailed' | 'scheduledFailed' | 'exportFailed'>): string {
  if (!day.outcomesRecorded) return NOT_MEASURED
  const part = (n: number | null) => (n == null ? NOT_MEASURED : exactNumber(n))
  return `${part(day.interactiveFailed)} · ${part(day.scheduledFailed)} · ${part(day.exportFailed)}`
}

/** The caption beside the background-export figure: the run log's exports are not these. */
export const BACKGROUND_EXPORTS_ONLY = 'background exports only'

/** "2 of 14 failed" — a count of a count, never a rate. */
export function backgroundExportsText(exports: Pick<ReportDetailBackgroundExports, 'failedCount' | 'requestedCount'>): string {
  return `${exactNumber(exports.failedCount)} of ${exactNumber(exports.requestedCount)} failed`
}

/**
 * "Newest 10 of 37 failures" when the failures table is capped; null when it shows every failure in the window. A
 * capped list presented as the whole would be the same lie as a measured zero, one level up.
 */
export function recentFailuresCapText(exports: Pick<ReportDetailBackgroundExports, 'failedCount' | 'recentFailures'>): string | null {
  const shown = exports.recentFailures.length
  if (exports.failedCount <= shown) return null
  return `Newest ${exactNumber(shown)} of ${exactNumber(exports.failedCount)} failures`
}

/** What a withheld person reads as: never a blank that looks like "nobody". */
export const PERSONAL_SCOPE_WITHHELD = 'Withheld — not in a shared workspace'

/**
 * Who requested a failed export: the stamped name, or {@link PERSONAL_SCOPE_WITHHELD} when the server withheld it
 * (the report is not in a shared workspace), or NOT_MEASURED when none was stamped.
 */
export function exportRequesterText(failure: Pick<ReportDetailExportFailure, 'requestedByDisplayName' | 'requesterWithheld'>): string {
  if (failure.requesterWithheld) return PERSONAL_SCOPE_WITHHELD
  return failure.requestedByDisplayName?.trim() || NOT_MEASURED
}

// ---------------------------------------------------------------------------
// Durations
// ---------------------------------------------------------------------------

/** "850 ms", "4.2 s", "3m 10s" — or NOT_MEASURED below the percentile's minimum sample. */
export function durationMsText(ms: number | null): string {
  if (ms == null) return NOT_MEASURED
  if (ms < 1000) return `${exactNumber(ms)} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return shortDuration(ms / 1000)
}

/** The sample behind the percentiles, ALWAYS shown, with the cap when it was hit. */
export function sampleText(
  durations: Pick<ReportDetailDurations, 'durationSampleCount' | 'sampleTruncated' | 'sampleCap'>,
  since: string | null = null,
): string {
  const n = `n = ${exactNumber(durations.durationSampleCount)}${since ? ` ${since}` : ''}`
  return durations.sampleTruncated ? `${n} (the newest ${exactNumber(durations.sampleCap)}; more exist)` : n
}

/**
 * Where a source's timings start when that is NOT the card's window: "since 2026-09-20 UTC" for the interactive
 * timings while the outcome window starts inside the page's window — they are read only from the cutover, under a
 * card titled for the whole window — and null otherwise. Scheduled timings span the whole window (the server says
 * why), so they never carry it.
 */
export function durationsSinceText(source: string, outcomes: ReportDetailOutcomeWindow): string | null {
  if (source !== 'Interactive' || outcomes.from == null || outcomes.coversWholeWindow) return null
  const day = utcDay(outcomes.from)
  return day ? `since ${day} UTC` : null
}

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

/** A schedule's last outcome, by name: all seven literals the delivery job writes, plus Unrecognised. */
export const SCHEDULE_OUTCOME_LABEL: Readonly<Record<string, string>> = {
  Delivered: 'Delivered',
  DeliveredPossiblyTruncated: 'Delivered (possibly truncated)',
  DeliveredAsLink: 'Delivered as a link',
  DeliveredAsLinkPossiblyTruncated: 'Delivered as a link (possibly truncated)',
  FailedTransient: 'Failed (transient)',
  FailedPermanent: 'Failed (permanent)',
  FailedUnexpected: 'Failed (unexpected error)',
  Unrecognised: 'Unrecognised status',
}

/** The last outcome in words; "Never run" for null; a name this build does not know, as sent. */
export function scheduleOutcomeLabel(outcome: string | null): string {
  if (outcome == null) return 'Never run'
  return SCHEDULE_OUTCOME_LABEL[outcome] ?? outcome
}

/**
 * How an outcome is painted. THERE IS NO "good": a last delivery says nothing
 * about the next one and nothing records a recipient reading it, so a delivery is
 * neutral, a possibly-truncated one is a caveat, a failure is a failure, and an
 * unrecognised status is neutral — never a guess.
 */
export type OutcomeTone = 'failed' | 'caveat' | 'neutral'

export function scheduleOutcomeTone(outcome: string | null): OutcomeTone {
  if (outcome === 'FailedTransient' || outcome === 'FailedPermanent' || outcome === 'FailedUnexpected') return 'failed'
  if (outcome === 'DeliveredPossiblyTruncated' || outcome === 'DeliveredAsLinkPossiblyTruncated') return 'caveat'
  return 'neutral'
}

/** A tone's badge colour. No green, by construction. */
export function toneColor(tone: OutcomeTone): 'red' | 'yellow' | 'gray' {
  return tone === 'failed' ? 'red' : tone === 'caveat' ? 'yellow' : 'gray'
}

/** Why a schedule's last run failed — the category only, never the stored text. */
const SCHEDULE_ERROR_LABEL: Readonly<Record<string, string>> = {
  ParameterBinding: 'Parameter binding failed',
  ModelDrift: 'No longer valid against the model',
  ReportMissing: 'Report no longer exists',
  ReportNotRunnable: 'Report not runnable',
  TooLargeNoStorage: 'Too large to attach, and no storage for a link',
  // The job's own refusals: nothing it depends on failed, and a person has to act.
  RunAsAccessLost: 'Paused — its run-as user can no longer schedule the report',
  AwaitingConfirmation: 'Waiting for its run-as user to confirm it',
  // The engine refused the report as it stands: nothing was sent, and its owner has to repair the report.
  QueryRefused: 'Refused by the query engine — the report needs repair',
  // Another delivery of the same schedule was still sending: nothing failed, and this one is retried.
  DeliveryOverlap: 'Waited for another delivery of this schedule — retried',
  DeliveryService: 'Refused by the delivery service',
  Unexpected: 'Unexpected error',
}

export function scheduleErrorLabel(category: string | null): string | null {
  return category == null ? null : SCHEDULE_ERROR_LABEL[category] ?? category
}

/** Why a background export failed — the category only. */
const EXPORT_ERROR_LABEL: Readonly<Record<string, string>> = {
  ParameterBinding: 'Parameter binding failed',
  ModelDrift: 'No longer valid against the model',
  ReportMissing: 'Report no longer exists',
  NoStorage: 'No storage configured',
  // The job's refusals of the view the export was asked with: exporting again answers both.
  ReportChangedSinceAsked: 'The report changed after it was asked for',
  ViewUnreadable: 'The view it was asked with could not be read',
  // The engine refused the report as it stands: nothing was run, and its owner has to repair the report.
  QueryRefused: 'Refused by the query engine — the report needs repair',
  Unexpected: 'Unexpected error',
}

export function exportErrorLabel(category: string): string {
  return EXPORT_ERROR_LABEL[category] ?? category
}

/** Why a schedule shows no next occurrence. */
const NEXT_OCCURRENCE_REASON_LABEL: Readonly<Record<string, string>> = {
  Paused: 'Paused — fires nothing',
  ReportNotRunnable: 'The report is not runnable, so the delivery job refuses every firing',
  InvalidCron: 'The cron expression does not parse',
  UnknownTimeZone: 'The time zone does not resolve',
  NoneWithinHorizon: 'Nothing fires within the next year',
}

export function nextOccurrenceReasonLabel(reason: string | null): string {
  if (reason == null) return NOT_MEASURED
  return NEXT_OCCURRENCE_REASON_LABEL[reason] ?? reason
}

/** A recipient count over the stored list, or NOT_MEASURED when the list is unreadable. */
export function recipientSlotsText(slots: number | null): string {
  return slots == null ? `${NOT_MEASURED} (list unreadable)` : exactNumber(slots)
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

/**
 * Distinct recipient addresses over active schedules, or NOT_MEASURED with how
 * many lists could not be read. A partial count is never served as a whole one.
 */
export function recipientsView(subscriptions: Pick<ReportDetailSubscriptions, 'distinctRecipientAddresses' | 'unreadableRecipientLists'>): FigureView {
  if (subscriptions.distinctRecipientAddresses == null) {
    const n = subscriptions.unreadableRecipientLists
    return {
      value: NOT_MEASURED,
      reason: `${exactNumber(n)} recipient ${n === 1 ? 'list' : 'lists'} could not be read, so no address is counted`,
    }
  }
  return { value: exactNumber(subscriptions.distinctRecipientAddresses), reason: null }
}

/** What the personal-scope counts say instead of names. */
export const NAMES_WITHHELD = 'names withheld'

/** "will not deliver: report is Archived", for a report that is not runnable; null for one that is. */
export function notDeliveringText(report: { status: string; isRunnable: boolean } | null | undefined): string | null {
  if (!report || report.isRunnable) return null
  return `will not deliver: report is ${report.status}`
}

/**
 * The label for the "would deliver" figure. NOT "delivering": whether Hangfire registered each schedule is invisible
 * to the API (D14), so the figure says only that a schedule is active and the report can run.
 */
export const WOULD_DELIVER_LABEL = 'Would deliver (active, report runnable)'

/** "2 active, 1 paused; 2 would deliver (report runnable)". */
export function schedulesSummaryText(active: number, paused: number, wouldDeliver: number): string {
  return `${exactNumber(active)} active, ${exactNumber(paused)} paused; ${exactNumber(wouldDeliver)} would deliver `
    + '(report runnable)'
}

/**
 * The active-days figure's label. Where opens of the report are recorded it counts days with an interactive run or an
 * export; where they are not — no parameters — it can only ever see exports, and says so rather than claiming opens.
 */
export function activeDaysLabel(interactiveRecordable: boolean): string {
  return interactiveRecordable ? 'Active days (interactive or export)' : 'Days with an export (opens are not recorded)'
}

/** The per-person column header, on the same rule. */
export function personActiveDaysHeader(interactiveRecordable: boolean): string {
  return interactiveRecordable ? 'Active days' : 'Days with an export'
}

/**
 * Who a schedule runs as: the stamped name, or {@link PERSONAL_SCOPE_WITHHELD} when the server withheld it (the
 * report is not in a shared workspace), or the id when no name was stamped.
 */
export function runAsText(schedule: Pick<ReportDetailSchedule, 'runAsUserId' | 'runAsDisplayName' | 'runAsWithheld'>): string {
  if (schedule.runAsWithheld) return PERSONAL_SCOPE_WITHHELD
  if (schedule.runAsDisplayName?.trim()) return schedule.runAsDisplayName
  return schedule.runAsUserId ? `${schedule.runAsUserId} (name not stamped)` : NOT_MEASURED
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

/**
 * What `lastExecutionAt` is called. SavedReport.LastRunAt is set only by a RECORDED run, failed ones included: a
 * delivery, an export and a Spyglass open. For a report with no parameters an open recorded nothing before every open
 * was, so while its opens are not counted (`interactiveRecordable` false) it may be the last recorded delivery or
 * export and the label may not say "of any kind". An API that does not say (older than the fix) gets the label that
 * claims least.
 */
export function lastExecutionLabel(interactiveRecordable: boolean | undefined): string {
  return interactiveRecordable === true ? 'Last recorded execution of any kind' : 'Last recorded delivery or export'
}

/**
 * The grants card's subtitle. "Live" only while the report can run: an archived report's unexpired grants confer
 * view-only access (run, export and schedule return on restore), and another non-runnable status confers nothing to run.
 */
export function grantsSubtitle(report: Pick<ReportDetailReport, 'isRunnable' | 'status'>): string {
  if (report.isRunnable) return 'Live grants by principal type, schedules, and favourites'
  if (report.status === 'Archived') return 'Unexpired grants — view-only while archived — schedules, and favourites'
  return `Unexpired grants — the report cannot run while ${report.status} — schedules, and favourites`
}

/** What `lastNonScheduledRunAt` is: from the run log, so bounded by its reach. */
export const LAST_NON_SCHEDULED_LABEL = 'Last run that was not a scheduled delivery'

/**
 * The Archived banner: the SERVED caption, word for word.
 *
 * An archived report's grants still confer read-only access, definition
 * included, and run, export and schedule return on restore — the server says
 * so and cites where. This client never words it as "grants confer nothing".
 * Null for any other status, and null if an Archived report came with no
 * caption: then nothing is claimed rather than a sentence invented here.
 */
export function archivedBannerText(overview: Pick<ReportDetailOverview, 'report' | 'grantsCaption'>): string | null {
  if (overview.report.status !== 'Archived') return null
  return overview.grantsCaption?.trim() ? overview.grantsCaption : null
}

/** A reserved, unwritten governance fact's name. */
const NOT_RECORDED_LABEL: Readonly<Record<string, string>> = {
  Certification: 'Certification',
  Sensitivity: 'Sensitivity classification',
  Published: 'Published',
  ReplacementReport: 'Replacement report',
}

export function notRecordedLabel(field: string): string {
  return NOT_RECORDED_LABEL[field] ?? field
}

/** "2 group or role grants — group members not resolvable". */
export function groupOrRoleGrantsText(count: number): string {
  return `${exactNumber(count)} group or role ${count === 1 ? 'grant' : 'grants'} — ${GROUP_MEMBERS_NOT_RESOLVABLE}`
}

/** "3 user grants". */
export function userGrantsText(count: number): string {
  return `${exactNumber(count)} user ${count === 1 ? 'grant' : 'grants'}`
}

/** A principal id with its type when it is not a single user. Ids only: nothing resolves one to a name. */
export function principalText(id: string | null, type: string | null): string {
  if (id == null) return NOT_MEASURED
  return type && type !== 'User' ? `${id} (${type})` : id
}

/**
 * The owner, read under the registry's rule: the stamped name only while the
 * owner is still the creator, an id otherwise ("name not recorded"), and a group
 * or role owner never as a person.
 */
export function ownerText(owner: ReportDetailOwner): string {
  if (owner.ownerUserId == null) return 'No owner recorded'
  if (owner.ownerPrincipalType && owner.ownerPrincipalType !== 'User') {
    return `${owner.ownerPrincipalType} ${owner.ownerUserId} — not a person; ${GROUP_MEMBERS_NOT_RESOLVABLE}`
  }
  const who = owner.ownerDisplayName ?? `${owner.ownerUserId} (name not recorded)`
  return owner.ownerIsCreatorFallback ? `${who} — the creator; no business owner is recorded` : who
}

/** Where the report is filed. */
export function workspaceText(overview: Pick<ReportDetailOverview, 'workspaceName' | 'workspaceId' | 'isPersonalWorkspace' | 'isUnfiled'>): string {
  if (overview.isUnfiled || overview.workspaceId == null) return 'Not filed — reachable by ownership and grants only'
  const name = overview.workspaceName ?? `${overview.workspaceId} (name not recorded)`
  return overview.isPersonalWorkspace ? `${name} (personal workspace)` : name
}

// ---------------------------------------------------------------------------
// Governance
// ---------------------------------------------------------------------------

/** "Showing 100 of 240" — the capped list beside the measured total. */
export function showingText(shown: number, total: number): string {
  return `Showing ${exactNumber(shown)} of ${exactNumber(total)}`
}

/** Where the names behind a transfer are: the audit trail, which stamps who acted. */
export const RESPONSIBILITY_HISTORY_POINTER =
  'Who performed each transfer, as the audit trail recorded them, is on the History tab.'

/** A retirement signal's name, as the candidate list words it. */
export function retirementSignalLabel(name: string): string {
  return reasonLabel(name)
}

/** The thresholds the signals were judged against, in the server's numbers. */
export function retirementThresholdText(retirement: Pick<ReportDetailRetirement, 'idleThresholdDays' | 'unreadWindowDays'>): string {
  return `Idle: no recorded run for ${exactNumber(retirement.idleThresholdDays)} days — ${IDLE_RUN_MEANING}. `
    + `Scheduled, not opened in `
    + `Cartographer: delivered on a schedule with no recorded open there in the last `
    + `${exactNumber(retirement.unreadWindowDays)} days — evaluated only where opens of the report are recorded.`
}

/**
 * The retirement reading in one sentence. Out of the population: the server's
 * reason. No signal firing is said only as far as it was evaluated — when the
 * log does not cover the unread window, or opens of the report are not recorded
 * at all (it declares no parameters), "Scheduled, not opened" was NOT evaluated,
 * and the server's reason is said rather than counted as clear.
 */
export function retirementSummary(retirement: ReportDetailRetirement): string {
  if (!retirement.inPopulation) return retirement.notInPopulationReason ?? 'Not a retirement candidate.'
  const evaluated = retirement.scheduledUnreadEvaluated ?? retirement.runLogCoversUnreadWindow
  const reason = retirement.scheduledUnreadNotEvaluatedReason?.trim()
    || (retirement.runLogCoversUnreadWindow ? 'it was not evaluated.' : 'the run log does not cover its window.')
  const unevaluated = evaluated ? '' : ` Scheduled, not opened in Cartographer: ${reason}`
  if (retirement.signals.length === 0) return `No retirement signal fires.${unevaluated}`
  return `${retirement.signals.map(retirementSignalLabel).join(', ')}.${unevaluated}`
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

/** What the audit trail records against a report's own id. */
export const AUDITED_FOR_REPORT: readonly string[] = [
  'Grants, role changes and revocations',
  'Business-owner and maintainer transfers',
  'Filing into or out of a workspace',
  'Archive, restore and purge',
  'Delists, their reversals, and keeps',
  'Orphan flags',
  'Runs of the saved report that carried custom joins',
]

/** What changes a report and is NOT audited anywhere. */
export const NOT_AUDITED: readonly string[] = [
  'Definition edits',
  'Renames',
  'Schedule changes',
  'Favourites',
  'Runs (other than the custom-join executions above)',
]

/** Why a report's access requests are not in its trail. */
export const ACCESS_REQUESTS_NOT_HERE =
  'Access-request rows are keyed by the request’s own id, not the report’s, so they are not shown here; a grant an '
  + 'approved request made is, as a Grant.'

/** The audit trail's retention. */
export const AUDIT_NEVER_TRIMMED = 'The audit trail is never trimmed.'

/** Why there are two links. */
export const TRAILS_NOT_MERGED =
  'Membership changes are recorded against the workspace, not the report, so they are a separate trail. The two are '
  + 'not merged here.'

/** The Audit Log, narrowed to one entity. */
export function auditLogPath(entityId: string): string {
  return `/audit?entityId=${encodeURIComponent(entityId)}`
}

/** The workspace trail's link text. */
export function membershipLinkLabel(workspaceName: string | null): string {
  return `Membership changes in ${workspaceName ?? 'this workspace'}`
}

// ---------------------------------------------------------------------------
// Errors — each tab fails alone, and loading, restricted, missing and broken differ
// ---------------------------------------------------------------------------

/** The migration the Access tab's read depends on. */
export const ACCESS_MIGRATION = 'AddGrantExpiryAndNotes'

/** The migration the Governance tab's episodes panel depends on. */
export const EPISODES_MIGRATION = 'AddRetirementCandidate'

export type TabErrorView =
  | { kind: 'restricted'; reason: string | null }
  | { kind: 'missing' | 'refused' | 'broken' | 'unreachable'; title: string; message: string }

/**
 * How a tab's failed read renders, by status:
 *
 * - 403: restricted (RestrictedState), neutral — the caller lacks a role.
 * - 404: "No such report" — the id matches nothing; every status is served, so
 *   it is not an archived report being hidden.
 * - 400: the window was not offered (7, 30 or 90), in the server's words.
 * - 5xx: names the migration when the read depends on one (`migration`), and
 *   otherwise says that it depends on none — so a 500 is never mistaken for an
 *   unapplied migration, nor an unapplied migration for a fault.
 */
export function tabErrorView(error: unknown, what: string, migration?: string): TabErrorView {
  const kind = errorKind(error)
  const detail = isApiError(error) ? error.detail : null

  switch (kind) {
    case 'forbidden':
      return { kind: 'restricted', reason: detail }
    case 'not-found':
      return {
        kind: 'missing',
        title: 'No such report',
        message: 'The server has no report with this id. Every lifecycle status is served here, Deleted included, so '
          + 'this is not an archived or delisted report being hidden — the id itself matches nothing.',
      }
    case 'bad-request':
      return {
        kind: 'refused',
        title: 'Window not offered',
        message: `${detail ?? 'days must be 7, 30 or 90'}. Choose 7, 30 or 90 days.`,
      }
    case 'server':
      return migration
        ? {
            kind: 'broken',
            title: `Could not load ${what} — ${migration} may not be applied`,
            message: `The API failed while reading ${what}. The likeliest cause outside local dev is migration `
              + `${migration} not having been applied: this read depends on it, and migrations here are applied by `
              + 'hand. This says nothing about the report itself.',
          }
        : {
            kind: 'broken',
            title: `Could not load ${what}`,
            message: `${errorSummary(error) ?? 'The server failed.'} This read depends on no pending migration, so `
              + 'this is a server fault, not missing data.',
          }
    case 'unauthorized':
      return {
        kind: 'refused',
        title: 'Not signed in',
        message: "The API did not accept this session's credentials. Sign in again, or check the API key.",
      }
    case 'network':
      return {
        kind: 'unreachable',
        title: 'Could not reach the API',
        message: `The request never got an answer. This says nothing about ${what}.`,
      }
    default:
      return {
        kind: 'broken',
        title: `Could not load ${what}`,
        message: errorSummary(error) ?? 'The request failed.',
      }
  }
}
